/**
 * Internal Traffic API Helper
 * 
 * Provides utilities for making API calls from admin/operator surfaces
 * that should be tagged as internal traffic and filtered from
 * production metrics and Guardian alerts.
 * 
 * Usage:
 * - Use `internalFetch()` for fetch-based API calls
 * - Use `getInternalHeaders()` to get headers for axios/other clients
 * 
 * Reasons:
 * - 'admin': Admin dashboard operations
 * - 'agent': Internal AI agent/bot operations
 * - 'guardian': Guardian console monitoring
 * - 'manual-test': Manual testing by developers
 */

// Internal traffic headers - signals to backend that this is operator traffic
const INTERNAL_TRAFFIC_HEADERS = {
  'X-Internal-Agent': '1',
};

/**
 * Get internal traffic headers with specific reason
 * @param {string} reason - Reason for internal traffic (admin, agent, guardian, manual-test)
 * @returns {Object} Headers object to merge with other headers
 */
export const getInternalHeaders = (reason = 'admin') => ({
  ...INTERNAL_TRAFFIC_HEADERS,
  'X-Internal-Reason': reason,
});

/**
 * Get auth + internal headers combined
 * @param {string} reason - Reason for internal traffic
 * @returns {Object} Headers object with both auth and internal traffic markers
 */
export const getAuthInternalHeaders = (reason = 'admin') => {
  const token = localStorage.getItem('token');
  return {
    ...getInternalHeaders(reason),
    Authorization: token ? `Bearer ${token}` : '',
  };
};

/**
 * Fetch wrapper that automatically tags requests as internal traffic
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {string} reason - Internal traffic reason
 * @returns {Promise<Response>} Fetch response
 */
export const internalFetch = async (url, options = {}, reason = 'admin') => {
  const headers = {
    ...options.headers,
    ...getInternalHeaders(reason),
  };
  
  return fetch(url, {
    ...options,
    headers,
  });
};

/**
 * Create axios request config with internal traffic headers
 * @param {string} token - Auth token
 * @param {string} reason - Internal traffic reason
 * @returns {Object} Axios config object
 */
export const createInternalAxiosConfig = (token, reason = 'admin') => ({
  headers: {
    Authorization: token ? `Bearer ${token}` : '',
    ...getInternalHeaders(reason),
  },
});

export default {
  getInternalHeaders,
  getAuthInternalHeaders,
  internalFetch,
  createInternalAxiosConfig,
};
